import React, { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import { gql } from '@yi/core';

import { sizeMobile } from '~/styles';

import { BaseCarousel, BaseCarouselComposerProps, createSlickSettings } from './BaseCarousel';
import { CarouselPlaceholder } from './CarouselPlaceholder';
import { WithPlaceholder } from './types';
import { MeditationCard } from '../cards';

const overrideSlickCarouselCSS = css`
  .slick-slide {
    padding: 0 4px;
    ${sizeMobile(css`
      display: flex;
      flex-direction: column;
      align-items: center;
    `)};
  }
`;
type CarouselItem = gql.typesClient.Fragment_MeditationCard & {
  style?: { url_title?: string }[];
};
type Props = BaseCarouselComposerProps<CarouselItem> & {
  dimension?: {
    width: number;
    height: number;
  };
};

type PlaceholderTypes = Pick<Props, 'button' | 'dimension' | 'headerCSS' | 'heading'>;
const Placeholder: React.FC<PlaceholderTypes> = ({
  button,
  dimension = MeditationCard.dimension,
  ...props
}) => (
  <CarouselPlaceholder
    cardDimension={dimension}
    headerDimensions={{ headerHeight: 15, headerWidth: 221 }}
    noButton={button === null}
    {...props}
  />
);

const defaultSlickSettings = createSlickSettings({
  sm: {
    slidesToShow: 2,
    slidesToScroll: 2,
    variableWidth: true,
    dots: false,
  },
  md: {
    slidesToShow: 4,
    slidesToScroll: 3,
    variableWidth: true,
  },
  lg: {
    slidesToShow: 4,
    slidesToScroll: 3,
    dots: false,
  },
  xl: {
    slidesToShow: 5,
    slidesToScroll: 3,
    dots: false,
  },
  xxl: {
    slidesToShow: 6,
    slidesToScroll: 3,
    variableWidth: true,
    dots: false,
  },
});
export const MeditationCarousel: React.FC<PropsWithChildren<Props>> &
  WithPlaceholder<PlaceholderTypes> = props => {
  if (!props.items?.length) return null;
  const { dimension, ...restProps } = props;
  return (
    <BaseCarousel<CarouselItem>
      slickSettings={defaultSlickSettings}
      renderCard={(cardProps: { item: CarouselItem }) => (
        <MeditationCard dimension={dimension} {...cardProps} />
      )}
      getReactKey={(i: CarouselItem) => i.title}
      overrideSlickCarouselCSS={overrideSlickCarouselCSS}
      {...restProps}
    />
  );
};

MeditationCarousel.Placeholder = Placeholder;
